import {
  faCircle,
  faRuler,
  faRulerHorizontal,
  faRulerVertical,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";
import Card from "src/components/Card";
import Typography from "src/components/Typography";
import {
  DECK_TYPE_OPTIONS,
  PROJECTILE_DIAMETER_OPTIONS,
  RAFTER_SPAN_OPTIONS,
  ROOF_LAYERS_OPTIONS,
  ROOF_PITCH_OPTIONS,
} from "src/pages/OrderCreate/Components/TestingParameters/types";
import { OrderDetailsProps } from "src/pages/OrderDetails/types";
import classes from "./styles.module.scss";

type Props = OrderDetailsProps;

const DamageInfo: React.FC<Props> = (data) => {
  const testing_parameters = data?.property_detail;

  // Helper function to get label from options
  const getOptionLabel = (
    value: string,
    options: { value: string; label: string }[]
  ) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  // Helper function to display deck type/thickness
  const getDeckTypeThickness = () => {
    if (testing_parameters?.deck_type_thickness === "Other - Fill in Blank") {
      return "Other - Fill in Blank";
    }
    return testing_parameters?.deck_type_thickness
      ? getOptionLabel(
          testing_parameters.deck_type_thickness,
          DECK_TYPE_OPTIONS
        )
      : "Not Provided";
  };

  // Helper function to get the other deck type value
  const getOtherDeckType = () => {
    return testing_parameters?.other_deck_type_thickness || "Not Provided";
  };

  // Check if "Other - Fill in Blank" is selected
  const isOtherDeckType =
    testing_parameters?.deck_type_thickness === "Other - Fill in Blank";

  if (!testing_parameters) {
    return (
      <div style={{ marginTop: "32px" }}>
        <Card>
          <div className={classes.damageInfo}>
            <Typography
              className={cx(classes.damageInfoText, "text-start")}
              fontWeight="semibold"
              variant="p18"
            >
              Damage Information
            </Typography>
            <div className={classes.damageInfoContent}>
              <Typography variant="p14" className="text-start">
                No damage information available for this order.
              </Typography>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "32px" }}>
      <Card>
        <div className={classes.damageInfo}>
          <Typography
            className={cx(classes.damageInfoText, "text-start")}
            fontWeight="semibold"
            variant="p18"
          >
            Damage Information
          </Typography>
          <div className={classes.damageInfoContent}>
            <div className="d-flex gap-3">
              <FontAwesomeIcon icon={faCircle} />
              <div>
                <Typography
                  className="text-start mb-0 text-uppercase"
                  variant="p12"
                  fontWeight="medium"
                >
                  Size of Hail
                </Typography>
                <Typography
                  className="text-start mb-0"
                  fontWeight="semibold"
                  variant="p16"
                >
                  {testing_parameters?.projectile_diameter ? (
                    getOptionLabel(
                      testing_parameters.projectile_diameter,
                      PROJECTILE_DIAMETER_OPTIONS
                    )
                  ) : (
                    <span className={classes.providedLabel}>Not Provided</span>
                  )}
                </Typography>
              </div>
            </div>
            <div className="d-flex gap-3">
              <FontAwesomeIcon icon={faRulerHorizontal} />
              <div>
                <Typography
                  className="text-start mb-0 text-uppercase"
                  variant="p12"
                  fontWeight="medium"
                >
                  Rafter Span
                </Typography>
                <Typography
                  className="text-start mb-0"
                  fontWeight="semibold"
                  variant="p16"
                >
                  {testing_parameters?.rafter_span ? (
                    getOptionLabel(
                      testing_parameters.rafter_span,
                      RAFTER_SPAN_OPTIONS
                    )
                  ) : (
                    <span className={classes.providedLabel}>Not Provided</span>
                  )}
                </Typography>
              </div>
            </div>
            <div className="d-flex gap-3">
              <FontAwesomeIcon icon={faRuler} />
              <div>
                <Typography
                  className="text-start mb-0 text-uppercase"
                  variant="p12"
                  fontWeight="medium"
                >
                  Number of Roof Layers
                </Typography>
                <Typography
                  className="text-start mb-0"
                  fontWeight="semibold"
                  variant="p16"
                >
                  {testing_parameters?.roof_layers ? (
                    getOptionLabel(
                      testing_parameters.roof_layers,
                      ROOF_LAYERS_OPTIONS
                    )
                  ) : (
                    <span className={classes.providedLabel}>Not Provided</span>
                  )}
                </Typography>
              </div>
            </div>
            <div className="d-flex gap-3">
              <FontAwesomeIcon icon={faRulerVertical} />
              <div>
                <Typography
                  className="text-start mb-0 text-uppercase"
                  variant="p12"
                  fontWeight="medium"
                >
                  Deck Type / Thickness
                </Typography>
                <Typography
                  className="text-start mb-0"
                  fontWeight="semibold"
                  variant="p16"
                >
                  {getDeckTypeThickness() !== "Not Provided" ? (
                    getDeckTypeThickness()
                  ) : (
                    <span className={classes.providedLabel}>Not Provided</span>
                  )}
                </Typography>
              </div>
            </div>
            {isOtherDeckType && (
              <div className="d-flex gap-3">
                <FontAwesomeIcon icon={faTag} />
                <div>
                  <Typography
                    className="text-start mb-0 text-uppercase"
                    variant="p12"
                    fontWeight="medium"
                  >
                    Other Deck Type
                  </Typography>
                  <Typography
                    className="text-start mb-0"
                    fontWeight="semibold"
                    variant="p16"
                  >
                    {getOtherDeckType() !== "Not Provided" ? (
                      getOtherDeckType()
                    ) : (
                      <span className={classes.providedLabel}>
                        Not Provided
                      </span>
                    )}
                  </Typography>
                </div>
              </div>
            )}
            <div className="d-flex gap-3">
              <FontAwesomeIcon icon={faRuler} />
              <div>
                <Typography
                  className="text-start mb-0 text-uppercase"
                  variant="p12"
                  fontWeight="medium"
                >
                  Pitch of Sample
                </Typography>
                <Typography
                  className="text-start mb-0"
                  fontWeight="semibold"
                  variant="p16"
                >
                  {testing_parameters?.roof_pitch ? (
                    getOptionLabel(
                      testing_parameters.roof_pitch,
                      ROOF_PITCH_OPTIONS
                    )
                  ) : (
                    <span className={classes.providedLabel}>Not Provided</span>
                  )}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DamageInfo;
